import React, { useState } from 'react';
import { UserPlus, FileText, Calendar, Plus, Loader2, CheckCircle } from 'lucide-react';
import { formatLocalDateTime } from '../../../../../../utils/date';
import { Avatar } from '../../../../../dashboard/components/DashboardComponents';
import api from '../../../../../../services/api';

import './Sidebar.css';

const getInitials = (m) => {
    if (!m) return '?';
    const name = typeof m === 'object' ? (m.name || `${m.first_name || ''} ${m.last_name || ''}`) : String(m);
    if (!name || name.trim() === '') return '?';
    return name.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const formatUpdatedDate = (dateStr, isManager) => {
    return formatLocalDateTime(dateStr);
};

/**
 * Sidebar component for section navigation and management
 */
const Sidebar = ({
    sidebarSections,
    activeSection,
    editableData,
    isManager,
    setIsAssignModalOpen,
    setIsDocInfoModalOpen,
    assignments,
    rfpId,
    navigate,
    setHoveredTabDetails,
    isSidebarCollapsed,
    fetchOverview,
    fetchAssignedUsersData,
    onExtensionClick,
    isNoGo = false,
    isTeamLeadViewOnly = false
}) => {
    const [isAddingSection, setIsAddingSection] = useState(false);
    const [newSectionName, setNewSectionName] = useState('');
    const [isSubmittingInstruction, setIsSubmittingInstruction] = useState(false);
    const [confirmationData, setConfirmationData] = useState(null);
    const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSendInstruction = async () => {
        if (!newSectionName.trim()) return;
        setIsSubmittingInstruction(true);
        try {
            const res = await api.post(`/extraction/v1/${rfpId}/section-action`, { instruction: newSectionName });
            if (res.data) {
                setConfirmationData({
                    requestId: res.data.request_id,
                    message: res.data.message
                });
                setIsAddingSection(false);
            }
        } catch (error) {
            console.error("Error sending instruction:", error);
            alert("Failed to send instruction. Please try again.");
        } finally {
            setIsSubmittingInstruction(false);
        }
    };

    const handleConfirmDecision = async (decision) => {
        if (!confirmationData) return;
        setIsSubmittingDecision(true);
        try {
            const res = await api.post(`/extraction/v1/${rfpId}/section-action/confirm`, {
                request_id: confirmationData.requestId,
                decision: decision
            });
            
            if (res.data && res.data.deleted === true) {
                setConfirmationData(null);
                setIsSuccess(true);
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
                return;
            }
            
            setConfirmationData(null);
            setNewSectionName('');
            if (typeof fetchOverview === 'function') fetchOverview();
        } catch (error) {
            console.error("Error confirming decision:", error);
            alert("Failed to confirm decision. Please try again.");
        }
        setIsSubmittingDecision(false);
    };

    const statusLabel = String(
        editableData?.global_status?.label || 
        editableData?.global_status?.name || 
        editableData?.proposal_stage || 
        editableData?.PROPOSAL_STAGE || 
        editableData?.status || 
        ''
    ).toLowerCase();

    const statusCodeVal = Number(
        editableData?.global_status?.code || 
        editableData?.status_code || 
        editableData?.global_status_code || 
        editableData?.proposal_stage_code || 
        0
    );

    const isExpired = statusCodeVal === 56 || statusLabel.includes('expired');


    return (
        <div className={`final-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header-redesign">
                <div className="sidebar-header-top">
                    <h3>Sections</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            className="sidebar-header-action-btn"
                            onClick={() => setIsDocInfoModalOpen(true)}
                            title="Analyzed Documents Information"
                        >
                            <FileText size={14} />
                            <span>Docs</span>
                        </button>
                        {isManager && !isNoGo && (
                            <button
                                className="sidebar-header-action-btn"
                                onClick={() => {
                                    if (!isTeamLeadViewOnly && !isExpired) {
                                        setIsAssignModalOpen(true);
                                        if (typeof fetchOverview === 'function') {
                                            fetchOverview();
                                        }
                                        if (typeof fetchAssignedUsersData === 'function') {
                                            fetchAssignedUsersData();
                                        }
                                    }
                                }}
                                title={isTeamLeadViewOnly ? "Cannot assign in view-only mode" : isExpired ? "Cannot assign while proposal is expired" : "Assign sections to team members"}
                                disabled={isTeamLeadViewOnly || isExpired}
                                style={{
                                    opacity: (isTeamLeadViewOnly || isExpired) ? 0.5 : 1,
                                    cursor: (isTeamLeadViewOnly || isExpired) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <UserPlus size={14} />
                                <span>Assign</span>
                            </button>
                        )}
                    </div>
                </div>
                <p>Click to view details</p>
                {/* Add Section UI - Only available for Team Leads / Managers */}
                {isManager && !isNoGo && (
                    <div style={{ marginTop: '8px', padding: '0 4px' }}>
                        <button 
                            onClick={() => {
                                if (!isTeamLeadViewOnly && !isExpired) {
                                    setIsAddingSection(true);
                                }
                            }}
                            disabled={isTeamLeadViewOnly || isExpired}
                            style={{ 
                                display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', 
                                color: '#FF5526', background: 'transparent', border: '1px dashed #FFB399', 
                                borderRadius: '6px', padding: '8px 12px', cursor: (isTeamLeadViewOnly || isExpired) ? 'not-allowed' : 'pointer', width: '100%', 
                                justifyContent: 'center', transition: 'all 0.2s',
                                opacity: (isTeamLeadViewOnly || isExpired) ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => { if (!isTeamLeadViewOnly && !isExpired) e.currentTarget.style.background = '#FFF1EC'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                            <Plus size={14} /> Add Section
                        </button>
                    </div>
                )}
            </div>

            {/* Add Section Modal */}
            {isAddingSection && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.4)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        background: '#FFFFFF',
                        borderRadius: '12px',
                        padding: '24px',
                        width: '400px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#0F172A', fontFamily: 'system-ui, sans-serif' }}>Add New Section</h3>
                        
                        <textarea 
                            placeholder="Enter section name or description..."
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                            rows={4}
                            style={{ 
                                padding: '12px', 
                                fontSize: '14px', 
                                borderRadius: '8px', 
                                border: '1px solid #CBD5E1', 
                                outline: 'none', 
                                color: '#0F172A', 
                                fontFamily: 'system-ui, sans-serif',
                                resize: 'none',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}
                            autoFocus
                        />

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                            <button 
                                onClick={() => { setIsAddingSection(false); setNewSectionName(''); }}
                                disabled={isSubmittingInstruction}
                                style={{ fontSize: '14px', color: '#475569', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '6px', cursor: isSubmittingInstruction ? 'not-allowed' : 'pointer', padding: '8px 16px', fontWeight: '500', transition: 'background 0.2s', opacity: isSubmittingInstruction ? 0.7 : 1 }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSendInstruction}
                                disabled={isSubmittingInstruction}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#FFFFFF', background: '#FF5526', border: 'none', borderRadius: '6px', cursor: isSubmittingInstruction ? 'not-allowed' : 'pointer', padding: '8px 20px', fontWeight: '600', boxShadow: '0 1px 2px rgba(255, 85, 38, 0.2)', transition: 'background 0.2s', opacity: isSubmittingInstruction ? 0.7 : 1 }}
                            >
                                {isSubmittingInstruction && <Loader2 size={14} className="animate-spin" />}
                                Send
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmationData && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.4)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        background: '#FFFFFF',
                        borderRadius: '12px',
                        padding: '24px',
                        width: '400px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#0F172A', fontFamily: 'system-ui, sans-serif' }}>Confirm Action</h3>
                        
                        <p style={{ margin: 0, fontSize: '14px', color: '#334155', fontFamily: 'system-ui, sans-serif', lineHeight: '1.5' }}>
                            {confirmationData.message}
                        </p>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                            <button 
                                onClick={() => handleConfirmDecision('decline')}
                                disabled={isSubmittingDecision}
                                style={{ fontSize: '14px', color: '#475569', background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '6px', cursor: isSubmittingDecision ? 'not-allowed' : 'pointer', padding: '8px 16px', fontWeight: '500', transition: 'background 0.2s', opacity: isSubmittingDecision ? 0.7 : 1 }}
                            >
                                Decline
                            </button>
                            <button 
                                onClick={() => handleConfirmDecision('accept')}
                                disabled={isSubmittingDecision}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#FFFFFF', background: '#FF5526', border: 'none', borderRadius: '6px', cursor: isSubmittingDecision ? 'not-allowed' : 'pointer', padding: '8px 20px', fontWeight: '600', boxShadow: '0 1px 2px rgba(255, 85, 38, 0.2)', transition: 'background 0.2s', opacity: isSubmittingDecision ? 0.7 : 1 }}
                            >
                                {isSubmittingDecision && <Loader2 size={14} className="animate-spin" />}
                                Accept
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Overlay Modal */}
            {isSuccess && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 10000,
                    background: 'rgba(15, 23, 42, 0.4)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <style>
                        {`
                        @keyframes customBounceIn {
                            0% { transform: scale(0.3); opacity: 0; }
                            50% { transform: scale(1.05); opacity: 1; }
                            70% { transform: scale(0.9); }
                            100% { transform: scale(1); opacity: 1; }
                        }
                        `}
                    </style>
                    <div style={{
                        background: '#FFFFFF',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        width: '320px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'fadeIn 0.2s ease-out'
                    }}>
                        <div style={{
                            background: '#22C55E',
                            borderRadius: '50%',
                            padding: '12px',
                            color: 'white',
                            boxShadow: '0 10px 15px -3px rgba(34, 197, 94, 0.3)',
                            marginBottom: '16px',
                            animation: 'customBounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards'
                        }}>
                            <CheckCircle size={40} />
                        </div>
                        <h2 style={{ margin: 0, color: '#0F172A', fontFamily: 'system-ui, sans-serif', fontSize: '20px', fontWeight: '700' }}>Action Completed</h2>
                        <p style={{ margin: '8px 0 0', color: '#64748B', fontFamily: 'system-ui, sans-serif', fontSize: '14px' }}>Refreshing page...</p>
                    </div>
                </div>
            )}

            <div className="sidebar-sections-list">
                {sidebarSections.map((section) => {
                    const label = section.label || section.key;
                    const key = section.key || section.label;
                    const sectionId = section.id || section._id;

                    // Priority: Explicit assignments map (detailed) -> section.assigned_to (general)
                    const assignedMembers = section.assigned_users ||
                        assignments[label] || assignments[key] || assignments[sectionId] ||
                        assignments[label.toLowerCase()] || assignments[key.toLowerCase()] ||
                        section.assigned_to || [];
                    const isAssigned = Array.isArray(assignedMembers) ? assignedMembers.length > 0 : !!assignedMembers;

                    // Status Detection: Prefer nested status object from backend (code 51-55, label "Section...")
                    const statusObj = section.status && typeof section.status === 'object' ? section.status : {};
                    const statusCodeRaw = statusObj.code;
                    const statusLabelRaw = statusObj.label || (typeof section.status === 'string' ? section.status : '');

                    // Priority: Explicit status from data -> metadata overrides
                    let sectionStatus = statusLabelRaw ||
                        editableData?.SECTION_STATUS?.[key] || editableData?.section_status?.[key] ||
                        editableData?.SECTION_STATUS?.[label] || editableData?.section_status?.[label] || '';

                    // Override "Unassigned" status if we actually have assignments in our robust map
                    const currentStatusLower = String(sectionStatus).toLowerCase();
                    if (isAssigned && (currentStatusLower === '' || currentStatusLower === 'none' || currentStatusLower.includes('unassigned'))) {
                        sectionStatus = 'Section Assigned';
                    } else if (currentStatusLower === '' || currentStatusLower === 'none' || currentStatusLower.includes('unassigned')) {
                        sectionStatus = 'Unassigned';
                    }

                    // Map to proper case for UI display to match target design
                    let displayStatus = (sectionStatus && String(sectionStatus).length > 15) ? sectionStatus : sectionStatus?.charAt(0).toUpperCase() + sectionStatus?.slice(1).toLowerCase();

                    // Determine badge color class based on status (Prefer numeric code if available)
                    let statusClass = 'other';
                    const statusStr = String(sectionStatus).toLowerCase();
                    const statusCode = statusCodeRaw || section.status_code || parseInt(sectionStatus) || 0;
                    const finalCode = section.status_code || (section.status && typeof section.status === 'object' ? section.status.code : null) || parseInt(section.status) || statusCodeRaw;
                    const labelLower = String(
                        section.status?.label ||
                        section.status?.name ||
                        section.status ||
                        ''
                    ).toLowerCase();
                    const isAutoClosed = finalCode === 57 || labelLower.includes('auto-closed') || labelLower.includes('autoclosed') || labelLower.includes('auto closed');
                    const isSessionExpired = finalCode === 56 || labelLower.includes('session expired') || labelLower.includes('session-expired') || statusStr.includes('session expired') || statusStr.includes('session-expired');

                    if (statusCode === 50) {
                        displayStatus = 'Section Unassigned';
                        statusClass = 'unassigned';
                    } else if (statusCode === 51) {
                        displayStatus = 'Section Assigned';
                        statusClass = 'assigned';
                    } else if (statusCode === 52) {
                        displayStatus = 'Section In-Progress';
                        statusClass = 'in-progress';
                    } else if (statusCode === 53) {
                        displayStatus = 'Section Submitted for Review';
                        statusClass = 'submitted';
                    } else if (statusCode === 54) {
                        displayStatus = 'Section TL Approved';
                        statusClass = 'approved';
                    } else if (statusCode === 55) {
                        displayStatus = 'Section Revision Needed';
                        statusClass = 'revision';
                    } else if (isSessionExpired) {
                        displayStatus = 'Session Expired';
                        statusClass = 'session-expired';
                    } else if (isAutoClosed) {
                        displayStatus = 'Auto-Closed';
                        statusClass = 'auto-closed';
                    } else {
                        // Fallback to text matching
                        if (isSessionExpired) {
                            displayStatus = 'Session Expired';
                            statusClass = 'session-expired';
                        } else if (isAutoClosed) {
                            displayStatus = 'Auto-Closed';
                            statusClass = 'auto-closed';
                        } else if (statusCode === 54 || statusStr.includes('approved') || statusStr.includes('finalised')) {
                            displayStatus = 'Section TL Approved';
                            statusClass = 'approved';
                        } else if (statusCode === 55 || statusStr.includes('revision') || statusStr.includes('rejected')) {
                            displayStatus = 'Section Revision Needed';
                            statusClass = 'revision';
                        } else if (statusCode === 53 || statusStr.includes('submitted')) {
                            displayStatus = 'Section Submitted for Review';
                            statusClass = 'submitted';
                        } else if (statusCode === 52 || statusStr.includes('progress')) {
                            displayStatus = 'Section In-Progress';
                            statusClass = 'in-progress';
                        } else if (statusStr.includes('unassigned')) {
                            displayStatus = 'Section Unassigned';
                            statusClass = 'unassigned';
                        } else if (statusCode === 51 || statusStr.includes('assigned')) {
                            displayStatus = 'Section Assigned';
                            statusClass = 'assigned';
                        }
                    }

                    // Strict Team Member Override based on status_code
                    if (!isManager && !isAutoClosed && !isSessionExpired) {
                        const code = section.status_code || (section.status && typeof section.status === 'object' ? section.status.code : null) || parseInt(section.status) || statusCodeRaw;
                        if (code === 51) {
                            displayStatus = 'Section Assigned';
                            statusClass = 'assigned';
                        } else if (code === 52) {
                            displayStatus = 'Section In-Progress';
                            statusClass = 'in-progress';
                        } else if (code === 53) {
                            displayStatus = 'Section Submitted for Review';
                            statusClass = 'submitted';
                        } else if (code === 54) {
                            displayStatus = 'Section TL Approved';
                            statusClass = 'approved';
                        } else if (code === 55) {
                            displayStatus = 'Section Revision Needed';
                            statusClass = 'revision';
                        }
                    }

                    return (
                        <button
                            key={sectionId || key}
                            className={`sidebar-section-tab ${activeSection === label ? 'active' : ''}`}
                            onClick={() => {
                                navigate(`/proposals/analysis-result/${rfpId}/secid/${sectionId}`, { replace: true });
                            }}
                            onMouseMove={(e) => {
                                const assignedRaw = section.assigned_users ||
                                    assignments[label] || assignments[key] || assignments[sectionId] ||
                                    assignments[label.toLowerCase()] || assignments[key.toLowerCase()] ||
                                    section.assigned_to || [];
                                const assignedArray = Array.isArray(assignedRaw) ? assignedRaw : [assignedRaw];
                                const memberNames = assignedArray.map(m => typeof m === 'object' ? (m.name || `${m.first_name} ${m.last_name}`) : m);

                                setHoveredTabDetails({
                                    key: label,
                                    assignedMembers: memberNames,
                                    isAssigned: memberNames.length > 0,
                                    top: e.clientY + 15,
                                    left: e.clientX + 15,
                                    notes: section.notes
                                });
                            }}
                            onMouseLeave={() => setHoveredTabDetails(null)}
                        >
                            <div className="section-tab-header">
                                <div className="section-tab-title">{label}</div>

                                {(() => {
                                    const extData = assignments[`ext_${sectionId}`] ||
                                        assignments[`ext_${key}`] ||
                                        assignments[`ext_${label}`] ||
                                        assignments[`ext_${label.toLowerCase()}`] || {};

                                    const hasPendingExtension = extData.status === 1 || section.extension_status === 1;

                                    if (isManager && hasPendingExtension) {
                                        return (
                                            <Calendar
                                                className="extension-calendar-icon"
                                                size={16}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const enrichedSection = {
                                                        ...section,
                                                        extension_status: 1,
                                                        extension_request: extData.request || section.extension_request
                                                    };
                                                    if (onExtensionClick) onExtensionClick(enrichedSection);
                                                }}
                                                title="Pending Deadline Extension Request"
                                            />
                                        );
                                    }
                                    return null;
                                })()}
                            </div>



                            <div className="section-tab-meta" style={{ display: 'flex', flexDirection: 'column', marginTop: '2px', width: '100%', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '6px' }}>
                                    {sectionStatus && (
                                        <div
                                            className={`section-approved-badge ${statusClass}`}
                                            style={{ maxWidth: '170px', flexShrink: 1, overflow: 'hidden' }}
                                            title={displayStatus}
                                        >
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                                {displayStatus}
                                            </span>
                                        </div>
                                    )}

                                    {isManager && isAssigned && (
                                        <div className="avatar-stack" style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}>
                                            {(Array.isArray(assignedMembers) ? assignedMembers : [assignedMembers]).slice(0, 2).map((member, idx) => {
                                                const mName = typeof member === 'object' ? (member.name || `${member.first_name || ''} ${member.last_name || ''}`) : String(member);
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="avatar-circle-sidebar-wrapper"
                                                        title={mName}
                                                        style={{
                                                            marginLeft: idx > 0 ? '-6px' : '0',
                                                            zIndex: 10 - idx,
                                                            flexShrink: 0,
                                                            display: 'flex',
                                                            borderRadius: '50%'
                                                        }}
                                                    >
                                                        <Avatar name={mName} size={20} />
                                                    </div>
                                                );
                                            })}
                                            {(Array.isArray(assignedMembers) ? assignedMembers : [assignedMembers]).length > 2 && (
                                                <div
                                                    className="avatar-circle-sidebar plus-more"
                                                    title={(Array.isArray(assignedMembers) ? assignedMembers : [assignedMembers]).map(m => typeof m === 'object' ? (m.name || `${m.first_name || ''} ${m.last_name || ''}`) : String(m)).join(', ')}
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        background: '#CBD5E1',
                                                        color: '#475569',
                                                        fontSize: '0.62rem',
                                                        fontWeight: '700',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: 'none',
                                                        marginLeft: '-6px',
                                                        zIndex: 5,
                                                        boxShadow: 'none',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    +{(Array.isArray(assignedMembers) ? assignedMembers : [assignedMembers]).length - 2}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', marginTop: '1px', marginLeft: '4px' }}>
                                    <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 500 }} title="Last Updated">
                                        {(() => {
                                            const getSectionUpdateTimestamp = () => {
                                                if (section.updated_at) {
                                                    return section.updated_at;
                                                }
                                                if (section.assignees && section.assignees.length > 0 && section.assignees[0].updated_at) {
                                                    return section.assignees[0].updated_at;
                                                }
                                                if (section.assigned_users && section.assigned_users.length > 0 && section.assigned_users[0].updated_at) {
                                                    return section.assigned_users[0].updated_at;
                                                }
                                                return editableData?.updated_at;
                                            };
                                            const timestamp = getSectionUpdateTimestamp();
                                            if (!timestamp) {
                                                return "No recent updates";
                                            }
                                            const formatted = formatUpdatedDate(timestamp, isManager);
                                            if (!formatted || formatted === '-') {
                                                return "No recent updates";
                                            }
                                            return `Updated at: ${formatted}`;
                                        })()}
                                    </span>
                                </div>
                            </div>

                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default Sidebar;
